import React from "react"
import PropTypes from "prop-types"
import { withScreenSize } from "@visx/visx"
import {
  Text,
  TextSM,
  TextXS,
  Flag,
  Box,
  Flex,
  Stack,
  useTheme,
} from "datastory-shared/ui"
import {
  format,
  formatDistance,
  startOfDay,
  addDays,
  addMonths,
  subWeeks,
} from "datastory-shared/utils/date"
import { Beeswarm } from "datastory-shared/data-ui/charts"

export const getChartData = data => {
  const grouped = []
  data.forEach(d => {
    const group = grouped.find(
      g => g.date === d.date && g.country.id === d.country.id
    )

    if (!group) grouped.push({ ...d, value: +new Date(d.date), elections: [d] })
    else group.elections.push(d)
  })

  return {
    byTime: { 1: grouped },
    domain: [+subWeeks(new Date(), 1), +addMonths(new Date(), 3)],
  }
}

const tooltipFormat = d => (
  <Box minWidth="240px" py={["xs", "xs", "sm"]} px={["xs", "xs", "sm"]}>
    <Stack spacing="xs">
      <TextSM>
        {format(
          new Date(d.date),
          {
            month: "short",
            day: "numeric",
          },
          "en"
        ).toUpperCase()}
      </TextSM>

      <Flex alignItems="center">
        {flagSymbol(d)}
        <TextSM fontWeight={600} ml="xs" fontSize="md">
          {d.country.name}
        </TextSM>
      </Flex>
      <Box>
        {d.elections.map(e => (
          <TextXS fontWeight={600} as="p" key={e.id}>
            {e.name}
          </TextXS>
        ))}
      </Box>
    </Stack>
  </Box>
)

const flagSymbol = d => (
  <Box
    data-cy="beeswarm-flag-symbol"
    borderRadius="full"
    w={["20px", null, "32px"]}
    h={["20px", null, "32px"]}
    position="relative"
    opacity={d.isPast ? 0.4 : ""}
    _hover={{
      boxShadow: ["none", "null", "0 0 0 0.25rem rgba(62,50,87,0.1)"],
    }}
  >
    <Flag
      variant="circle"
      name={d.country?.iso2}
      size={[24, null, 34]}
      borderWidth={1}
    />
  </Box>
)

export const Chart = withScreenSize(({ data, screenWidth: width }) => {
  const { breakpoints } = useTheme()
  // parse second smallest breakpoint as number, assumes 16px = 1rem
  const isMobile = width < parseFloat(breakpoints.md) * 16
  const upcomingElections = data.filter(d => !d.isPast)
  const chartData = getChartData(data)

  const today = startOfDay(new Date())
  const ticks = [+today, +addDays(today, 30), +addMonths(today, 2)]

  return (
    <Box mt={["sm", "sm", "xl"]} fontWeight="600">
      <Text textAlign="center">
        There are {upcomingElections.length} elections in the next three months
      </Text>
      <Box position="relative">
        <Beeswarm
          slider={false}
          side="negative"
          data={chartData}
          tickValues="on"
          ticks={ticks}
          radius={isMobile ? 11 : 17}
          symbolSize={isMobile ? 20 : 32}
          tickComponent={(tick, i) => (
            <>
              {i === 0 && (
                <Box position="absolute" bottom="100%" left="50%">
                  <svg
                    width="37"
                    height={isMobile ? "124" : "184"}
                    viewBox={isMobile ? "0 0 40 124" : "0 0 40 184"}
                    fill="none"
                  >
                    <line
                      x1="1.5"
                      y1={isMobile ? "124" : "186"}
                      x2="1.50001"
                      y2="1.5"
                      stroke="#ADA6BE"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <path
                      d="M3 4L34.6592 16.5706C35.5005 16.9046 35.5005 18.0954 34.6592 18.4294L3 31V4Z"
                      fill="#511EE9"
                    />
                  </svg>
                </Box>
              )}
              <TextXS fontWeight={600} muted>
                {formatDistance(
                  new Date(tick),
                  today,
                  { numeric: "auto" },
                  "en"
                )}
              </TextXS>
            </>
          )}
          symbol={flagSymbol}
          tooltipFormat={isMobile ? undefined : tooltipFormat}
        />
      </Box>
    </Box>
  )
})

Chart.propTypes = {
  data: PropTypes.array,
  screenWidth: PropTypes.number,
}
